import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ListChecks, Plus, CheckCircle2, Clock, X, Trash2 } from "lucide-react";
import HouseholdNav from "@/components/HouseholdNav";

const STATUS_TONE = {
    "Open":      "bg-amber-50 text-amber-800 border-amber-200",
    "Completed": "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Snoozed":   "bg-stone-100 text-stone-600 border-stone-200",
};

export default function HouseholdTasks() {
    const [rows, setRows]     = useState([]);
    const [meta, setMeta]     = useState({});
    const [members, setMembers] = useState([]);
    const [status, setStatus] = useState("Open");
    const [open, setOpen]     = useState(false);
    const [form, setForm]     = useState({ title: "", scope: "Review subscription", assigned_to: "", due_date: "", note: "" });

    const load = async () => {
        const [t_, h] = await Promise.all([
            api.get("/household/tasks", { params: status === "All" ? {} : { status } }).catch(() => null),
            api.get("/household").catch(() => null),
        ]);
        setRows(t_?.data?.tasks || []);
        setMeta(t_?.data?.meta || {});
        setMembers((h?.data?.members || []).filter(m => m.status === "Active"));
    };
    useEffect(() => { load(); }, [status]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title) return toast.error("Give the task a title");
        try {
            await api.post("/household/tasks", form);
            toast.success("Task added");
            setOpen(false);
            setForm({ title: "", scope: "Review subscription", assigned_to: "", due_date: "", note: "" });
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add"); }
    };

    const setStat = async (t_, next) => {
        try { await api.patch(`/household/tasks/${t_.id}`, { status: next }); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update"); }
    };

    const del = async (t_) => {
        if (!window.confirm("Delete this task?")) return;
        try { await api.delete(`/household/tasks/${t_.id}`); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Couldn't delete"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="household-tasks-page">
            <HouseholdNav />
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <ListChecks size={12} /> Household tasks
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                        Finance &amp; document tasks — assigned, not judged.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Keep tasks scoped to finance and documents. VAULT is not a generic todo — every task
                        should relate to something you already track here.
                    </p>
                </div>
                <button onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                        data-testid="task-add">
                    <Plus size={12} /> New task
                </button>
            </div>

            <div className="flex gap-1 border border-stone-200 rounded-full p-1 bg-white w-fit mb-4">
                {["Open", "Snoozed", "Completed", "All"].map(s => (
                    <button key={s} onClick={() => setStatus(s)}
                            className={`px-4 py-1.5 text-[12.5px] font-semibold rounded-full transition-colors ${status === s ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                            data-testid={`task-filter-${s}`}>
                        {s}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {rows.map(t_ => {
                    const assignee = members.find(m => m.user_id === t_.assigned_to);
                    return (
                        <div key={t_.id} className="rounded-xl border border-stone-200 bg-white p-3.5" data-testid={`task-${t_.id}`}>
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className={`text-[10.5px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_TONE[t_.status]}`}>
                                    {t_.status}
                                </span>
                                <span className="text-[10.5px] text-stone-500 font-semibold uppercase tracking-widest">{t_.scope}</span>
                                <button onClick={() => del(t_)} className="ml-auto text-stone-400 hover:text-rose-700" data-testid={`task-del-${t_.id}`}>
                                    <Trash2 size={11} />
                                </button>
                            </div>
                            <div className="text-[14px] font-semibold text-stone-900">{t_.title}</div>
                            <div className="text-[11.5px] text-stone-500 mt-0.5">
                                Assigned to {assignee?.name || assignee?.email || "you"} {t_.due_date && `· due ${t_.due_date}`}
                            </div>
                            {t_.note && <div className="text-[11.5px] text-stone-500 mt-1.5 italic">{t_.note}</div>}
                            <div className="mt-2.5 flex flex-wrap gap-1.5">
                                {t_.status !== "Completed" && (
                                    <button onClick={() => setStat(t_, "Completed")}
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-1"
                                            data-testid={`task-complete-${t_.id}`}>
                                        <CheckCircle2 size={10} /> Complete
                                    </button>
                                )}
                                {t_.status === "Open" && (
                                    <button onClick={() => setStat(t_, "Snoozed")}
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-700 border border-stone-300 rounded-full px-2.5 py-1"
                                            data-testid={`task-snooze-${t_.id}`}>
                                        <Clock size={10} /> Snooze
                                    </button>
                                )}
                                {t_.status !== "Open" && (
                                    <button onClick={() => setStat(t_, "Open")}
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-700 border border-stone-300 rounded-full px-2.5 py-1"
                                            data-testid={`task-reopen-${t_.id}`}>
                                        Reopen
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {rows.length === 0 && (
                    <div className="col-span-full text-center py-16 text-stone-500 text-[13px]" data-testid="task-empty">
                        No tasks in this view. Add one to organise a shared review or update.
                    </div>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4"
                     onClick={() => setOpen(false)} data-testid="task-form-modal">
                    <form onSubmit={submit} onClick={e => e.stopPropagation()}
                          className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-display text-stone-900">New household task</h3>
                            <button type="button" onClick={() => setOpen(false)}><X size={14} /></button>
                        </div>
                        <label className="block">
                            <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Title</span>
                            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
                                   className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px]"
                                   data-testid="task-title" required />
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Scope</span>
                                <select value={form.scope} onChange={e => setForm(f => ({...f, scope: e.target.value}))}
                                        className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px] bg-white"
                                        data-testid="task-scope">
                                    {(meta.scopes || []).map(s => <option key={s}>{s}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Assign to</span>
                                <select value={form.assigned_to} onChange={e => setForm(f => ({...f, assigned_to: e.target.value}))}
                                        className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px] bg-white"
                                        data-testid="task-assignee">
                                    <option value="">Me</option>
                                    {members.map(m => <option key={m.id} value={m.user_id}>{m.name || m.email}</option>)}
                                </select>
                            </label>
                            <label className="block col-span-2">
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Due date</span>
                                <input type="date" value={form.due_date}
                                       onChange={e => setForm(f => ({...f, due_date: e.target.value}))}
                                       className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px]"
                                       data-testid="task-due" />
                            </label>
                        </div>
                        <label className="block">
                            <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Note</span>
                            <textarea value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))}
                                      rows={2}
                                      className="mt-1 w-full border border-stone-300 rounded px-2 py-1.5 text-[13px]" />
                        </label>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="submit"
                                    className="text-[12px] font-semibold text-white bg-stone-900 hover:bg-stone-700 rounded-full px-3.5 py-1.5"
                                    data-testid="task-save">
                                Save task
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
